import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { toast } from "sonner";
import { FileTextIcon } from "@phosphor-icons/react/FileText";
import { type ShipmentDocument } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentDocument";
import { carriersApi } from "@/App/Auth/carrierApi";

/**
 * The paperwork for this shipment — the bill of lading we issued, the one the
 * driver signed, the proof of delivery, and any customs documents.
 *
 * Nothing else. Two of these we already email the carrier and two came from
 * them, so this is somewhere to find them again rather than new exposure. The
 * backend holds the allowlist; this only renders what it returns.
 */
export function ShipmentDocumentsTab({
  shipmentId,
  documents,
}: {
  shipmentId: string;
  documents: ShipmentDocument[] | undefined;
}) {
  const [downloading, setDownloading] = useState<string | undefined>(undefined);

  async function download(document_: ShipmentDocument) {
    setDownloading(document_.documentId);
    try {
      const content = await carriersApi().downloadCarrierShipmentDocument({
        shipmentId,
        documentId: document_.documentId,
      });
      saveBase64Pdf_(content.base64Content, content.fileName);
    } catch {
      toast.error("That document couldn't be downloaded. Try again shortly.");
    } finally {
      setDownloading(undefined);
    }
  }

  const columns: Column<ShipmentDocument>[] = [
    {
      key: "label",
      header: "Document",
      render: (document_) => <TextCell value={document_.label} />,
    },
    {
      key: "uploadedAt",
      header: "Added",
      render: (document_) => (
        <TextCell value={document_.uploadedAt ?? undefined} />
      ),
    },
  ];

  if (documents === undefined) {
    return (
      <Tab id="documents" title="Documents">
        <T size="sm" color="secondary">
          Loading…
        </T>
      </Tab>
    );
  }

  return (
    <Tab id="documents" title="Documents" count={documents.length}>
      {documents.length === 0 ? (
        <EmptyState
          icon={FileTextIcon}
          title="No documents yet"
          description="The bill of lading, proof of delivery and customs paperwork appear here as they're added."
        />
      ) : (
        <Stack gap={2}>
          <Table
            columns={columns}
            data={documents}
            rowKey={(document_) => document_.documentId}
            rowActions={(document_) => (
              <Button
                onClick={() => download(document_)}
                loading={downloading === document_.documentId}
              >
                Download
              </Button>
            )}
          />
        </Stack>
      )}
    </Tab>
  );
}

/**
 * Save a base64 PDF to the carrier's machine.
 *
 * The document comes back through the API rather than as a signed storage URL,
 * so the carrier scoping is re-checked on every fetch and there's no link that
 * outlives the check. The cost is that the browser has to do the saving.
 */
function saveBase64Pdf_(base64Content: string, fileName: string): void {
  const bytes = Uint8Array.from(atob(base64Content), (character) =>
    character.charCodeAt(0),
  );
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/pdf" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
